import { useState } from "react";
import L from "leaflet";
import { Marker, Polyline, Tooltip } from "react-leaflet";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Route, Check, Undo2 } from "lucide-react";
import { fmtHrs, roundQuarter } from "@/lib/dfr";

const OSRM = "https://router.project-osrm.org/route/v1/driving";

export const stopId = (s) => (s.end ? "home-end" : s.home ? "home" : s.row.id);
export const legKey = (from, to) => `${stopId(from)}|${stopId(to)}`;

const routeLeg = async (points) => {
  const coords = points.map((p) => `${p[1]},${p[0]}`).join(";");
  const res = await fetch(`${OSRM}/${coords}?overview=full&geometries=geojson&steps=false`);
  const data = await res.json();
  if (data.code !== "Ok" || !data.routes?.[0]) throw new Error(data.message || "No route found");
  const r = data.routes[0];
  return { line: r.geometry.coordinates.map(([lng, lat]) => [lat, lng]), miles: r.distance / 1609.34, hours: r.duration / 3600 };
};

/** Driving legs between consecutive stops (OSRM), each routed through the user's via points (vias["from|to"] = [[lat,lng],...]). */
export const fetchPlannedRoute = async (stops, vias = {}) => {
  const legs = await Promise.all(stops.slice(1).map(async (to, i) => {
    const from = stops[i];
    const key = legKey(from, to);
    const via = vias[key] || [];
    const r = await routeLeg([from.pos, ...via, to.pos]);
    return { from, to, key, vias: via, ...r };
  }));
  return { legs, line: legs.flatMap((l) => l.line), miles: legs.reduce((a, l) => a + l.miles, 0), hours: legs.reduce((a, l) => a + l.hours, 0) };
};

const handleIcon = L.divIcon({ className: "dfr-route-handle", html: '<div style="width:14px;height:14px;border-radius:9999px;background:#fff;border:3px solid #2563eb;box-shadow:0 1px 4px rgba(0,0,0,.5);cursor:grab"></div>', iconSize: [14, 14], iconAnchor: [7, 7] });
const viaIcon = L.divIcon({ className: "dfr-route-via", html: '<div style="width:14px;height:14px;border-radius:9999px;background:#2563eb;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5);cursor:grab"></div>', iconSize: [14, 14], iconAnchor: [7, 7] });

const dist = (a, b) => Math.hypot(a[0] - b[0], (a[1] - b[1]) * Math.cos((a[0] * Math.PI) / 180));
const nearestIndex = (line, p) => line.reduce((best, q, i) => (dist(q, p) < dist(line[best], p) ? i : best), 0);
// Point halfway along the leg by length (so the handle sits on the road, not between the stops)
const midpoint = (line) => {
  if (line.length < 2) return line[0];
  const seg = line.slice(1).map((q, i) => dist(line[i], q));
  const half = seg.reduce((a, b) => a + b, 0) / 2;
  let acc = 0;
  for (let i = 0; i < seg.length; i += 1) {
    if (acc + seg[i] >= half) { const t = seg[i] ? (half - acc) / seg[i] : 0; return [line[i][0] + (line[i + 1][0] - line[i][0]) * t, line[i][1] + (line[i + 1][1] - line[i][1]) * t]; }
    acc += seg[i];
  }
  return line[line.length - 1];
};

/** Planned route lines. With onViasChange: a white handle mid-leg can be dragged onto the road actually taken (adds a via point);
 *  blue via points drag to adjust and click to remove. */
export const PlannedRouteLine = ({ plan, onViasChange }) => {
  if (!plan) return null;
  const setLegVias = (leg, next) => onViasChange({ ...Object.fromEntries(plan.legs.map((l) => [l.key, l.vias])), [leg.key]: next });
  const addVia = (leg, ll) => {
    const p = [ll.lat, ll.lng];
    const midIdx = nearestIndex(leg.line, midpoint(leg.line));
    const at = leg.vias.findIndex((v) => nearestIndex(leg.line, v) > midIdx);
    const next = [...leg.vias];
    next.splice(at === -1 ? next.length : at, 0, p);
    setLegVias(leg, next);
  };
  return (
    <>
      {plan.legs.map((leg, i) => (
        <Polyline key={leg.key || i} positions={leg.line} pathOptions={{ color: "#2563eb", weight: 4, opacity: 0.85, dashArray: "10 8" }}>
          {onViasChange && <Tooltip sticky>Leg {i + 1} · {leg.miles.toFixed(1)} mi — drag the white handle onto the road you took</Tooltip>}
        </Polyline>
      ))}
      {onViasChange && plan.legs.map((leg, i) => (
        <Marker key={`h-${leg.key || i}`} position={midpoint(leg.line)} icon={handleIcon} draggable zIndexOffset={500} eventHandlers={{ dragend: (e) => addVia(leg, e.target.getLatLng()) }}>
          <Tooltip direction="top" offset={[0, -8]}>Drag to change leg {i + 1}'s road</Tooltip>
        </Marker>
      ))}
      {onViasChange && plan.legs.flatMap((leg) => leg.vias.map((v, j) => (
        <Marker key={`v-${leg.key}-${j}`} position={v} icon={viaIcon} draggable zIndexOffset={600}
          eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setLegVias(leg, leg.vias.map((x, k) => (k === j ? [ll.lat, ll.lng] : x))); }, click: () => setLegVias(leg, leg.vias.filter((_, k) => k !== j)) }}>
          <Tooltip direction="top" offset={[0, -8]}>Via point — drag to adjust · click to remove</Tooltip>
        </Marker>
      )))}
    </>
  );
};

export const RoutePlanner = ({ pins, plan, onPlan, onApply, homeSet, startHome, endHome, onStartHome, onEndHome, vias = {}, onResetVias, replanning = false }) => {
  const [busy, setBusy] = useState(false);
  const stops = pins.filter((p) => p.pos);
  const hasHome = stops.some((p) => p.home);
  const label = (s) => (s.home ? <span className="text-emerald-700">Home</span> : s.row.job || "Job");
  const viaCount = Object.values(vias).reduce((a, v) => a + (v?.length || 0), 0);

  const run = async () => {
    if (stops.length < 2 || !stops.some((p) => !p.home)) { toast.error("Need at least two stops with a location (Home + a job, or two job rows) to plan a route"); return; }
    setBusy(true);
    try { onPlan({ ...(await fetchPlannedRoute(stops, vias)), id: Date.now() }); }
    catch (e) { toast.error(`Route planning failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-blue-300 bg-blue-50/60 rounded p-2 space-y-2" data-testid="dfr-route-planner">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" onClick={run} disabled={busy || stops.length < 2} className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="dfr-plan-route-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Route className="w-3.5 h-3.5 mr-1" />}{plan ? "Re-plan" : "Plan route"}
        </Button>
        {plan && onApply && (
          <Button type="button" onClick={() => onApply(plan)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dfr-apply-route-btn"><Check className="w-3.5 h-3.5 mr-1" />Apply to report</Button>
        )}
        {viaCount > 0 && onResetVias && (
          <Button type="button" variant="outline" onClick={onResetVias} className="h-8 text-xs border-zinc-400" data-testid="dfr-route-reset-vias"><Undo2 className="w-3.5 h-3.5 mr-1" />Reset road edits ({viaCount})</Button>
        )}
        {replanning && <span className="text-[11px] text-blue-700 inline-flex items-center gap-1" data-testid="dfr-route-replanning"><Loader2 className="w-3 h-3 animate-spin" />Re-routing…</span>}
        <span className="text-xs font-mono uppercase tracking-widest text-blue-900 flex items-center gap-1 ml-1">No driving needed</span>
        <span className="text-[11px] text-zinc-600">{hasHome ? "Starts at your Home address, then the job pins in row order" : "Drive legs between the job pins in row order (set a Home address to plan the first leg too)"} — estimated miles and time to each next location.</span>
      </div>
      {plan && (
        <p className="text-[11px] text-blue-900" data-testid="dfr-route-edit-hint">
          Wrong road? Drag the <span className="inline-block w-2.5 h-2.5 rounded-full bg-white border-2 border-blue-600 align-middle" /> white handle on a leg onto the road you actually took — the leg re-routes through it. Drag a <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-600 border-2 border-white align-middle" /> via point to adjust it, click it to remove it. Edits are saved with the report.
        </p>
      )}
      {onStartHome && (
        <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-800" data-testid="dfr-route-home-options">
          <label className={`inline-flex items-center gap-1.5 ${homeSet ? "" : "opacity-50"}`} title={homeSet ? "" : "Set a Home address first"}>
            <input type="checkbox" checked={!!startHome && homeSet} disabled={!homeSet} onChange={(e) => onStartHome(e.target.checked)} data-testid="dfr-route-start-home" />Start from Home <span className="text-zinc-500">(fills first job's Travel to site)</span>
          </label>
          <label className={`inline-flex items-center gap-1.5 ${homeSet ? "" : "opacity-50"}`} title={homeSet ? "" : "Set a Home address first"}>
            <input type="checkbox" checked={!!endHome && homeSet} disabled={!homeSet} onChange={(e) => onEndHome(e.target.checked)} data-testid="dfr-route-end-home" />Return Home at end <span className="text-zinc-500">(fills last job's Return to Home)</span>
          </label>
          {!homeSet && <span className="text-[11px] text-amber-700">No Home address set — use the Home / start box above the map.</span>}
        </div>
      )}
      {plan && (
        <table className="w-full text-xs bg-white border border-zinc-300" data-testid="dfr-route-legs">
          <thead><tr className="bg-zinc-800 text-white"><th className="px-2 py-1 text-left">Leg</th><th className="px-2 py-1 text-left">From</th><th className="px-2 py-1 text-left">To</th><th className="px-2 py-1 text-right">Miles</th><th className="px-2 py-1 text-right">Drive time</th><th className="px-2 py-1 text-right">Hrs (¼)</th></tr></thead>
          <tbody>
            {plan.legs.map((l, i) => (
              <tr key={i} className={i % 2 ? "bg-zinc-50" : "bg-white"} data-testid={`dfr-route-leg-${i}`}>
                <td className="px-2 py-1">{i + 1}{l.vias?.length > 0 && <span className="ml-1 text-[10px] text-blue-700" title="Routed through your road edits" data-testid={`dfr-route-leg-edited-${i}`}>edited</span>}</td><td className="px-2 py-1 font-semibold">{label(l.from)}</td><td className="px-2 py-1 font-semibold">{label(l.to)}</td>
                <td className="px-2 py-1 text-right" data-testid={`dfr-route-leg-miles-${i}`}>{l.miles.toFixed(1)}</td><td className="px-2 py-1 text-right">{Math.round(l.hours * 60)} min</td><td className="px-2 py-1 text-right">{fmtHrs(roundQuarter(l.hours))}</td>
              </tr>
            ))}
            <tr className="bg-blue-100 font-semibold"><td className="px-2 py-1" colSpan={3}>Total</td><td className="px-2 py-1 text-right" data-testid="dfr-route-total-miles">{plan.miles.toFixed(1)}</td><td className="px-2 py-1 text-right">{Math.round(plan.hours * 60)} min</td><td className="px-2 py-1 text-right">{fmtHrs(roundQuarter(plan.hours))}</td></tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RoutePlanner;
